import {
    FLOW_CONTENT_CONTAINERS,
    FLOW_INITIAL_STEP_MODES,
    FLOW_PROGRESS_PERSISTENCE_MODES,
    FLOW_RENDERERS,
    FLOW_SAVE_STATUS_MODES,
    FLOW_STEPPER_SIZES,
    FLOW_MANIFEST_SCHEMA_VERSION,
    NAVIGATION_SCROLL_POLICIES,
    STEP_COMPLETION_MODES,
    STEP_MOUNT_MODES,
    STEP_REVEAL_MODES,
    STEP_VALIDATION_MODES,
    TERMINAL_INVALID_BEHAVIORS,
    TERMINAL_VALIDATION_SCOPES,
    UNAVAILABLE_BEHAVIORS,
} from '../schemas/flow-manifest-schema'

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const isPlainObject = value => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
)

const isJsonValue = (value, ancestors = new WeakSet()) => {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return true
    }

    if (typeof value === 'number') {
        return Number.isFinite(value)
    }

    if (typeof value !== 'object' || ancestors.has(value)) {
        return false
    }

    ancestors.add(value)

    const values = Array.isArray(value) ? value : Object.values(value)
    const valid = values.every(item => isJsonValue(item, ancestors))

    ancestors.delete(value)

    return valid
}

const hasEnumValue = (values, value) => values.includes(value)

const FLOW_RENDERER_BOOLEAN_SETTINGS = Object.freeze([
    'showStepTitle',
    'showStepDescription',
    'showPreviousButton',
    'showSaveStatusText',
    'contentBordered',
])

const FLOW_RENDERER_SETTING_NAMES = Object.freeze([
    'contentContainer',
    'saveStatus',
    'stepperSize',
    ...FLOW_RENDERER_BOOLEAN_SETTINGS,
])

const addError = (errors, code, path, value) => {
    errors.push({ code, path, value })
}

const validateValueAgainstSchema = (value, schema, path, errors) => {
    if (!schema || !schema.type) {
        return
    }

    if (schema.type === 'object') {
        if (!isPlainObject(value)) {
            addError(errors, 'INVALID_STEP_SETTING', path, value)
            return
        }

        const properties = schema.properties || {}

        Object.keys(value).forEach(key => {
            const propertyPath = `${path}.${key}`

            if (schema.additionalProperties === false && !hasOwn(properties, key)) {
                addError(errors, 'UNKNOWN_STEP_SETTING', propertyPath, value[key])
                return
            }

            validateValueAgainstSchema(value[key], properties[key], propertyPath, errors)
        })
        return
    }

    if (schema.type === 'boolean' && typeof value !== 'boolean') {
        addError(errors, 'INVALID_STEP_SETTING', path, value)
        return
    }

    if (schema.type === 'string' && typeof value !== 'string') {
        addError(errors, 'INVALID_STEP_SETTING', path, value)
        return
    }

    if (schema.type === 'array') {
        if (!Array.isArray(value)) {
            addError(errors, 'INVALID_STEP_SETTING', path, value)
            return
        }

        value.forEach((item, index) => validateValueAgainstSchema(
            item,
            schema.items,
            `${path}[${index}]`,
            errors,
        ))
    }

    if (schema.enum && !schema.enum.includes(value)) {
        addError(errors, 'INVALID_STEP_SETTING', path, value)
    }
}

const validateStepSettings = (settings, settingsSchema, path, errors) => {
    if (typeof settings === 'undefined') {
        return
    }

    if (!isPlainObject(settings) || !isJsonValue(settings)) {
        addError(errors, 'INVALID_STEP_SETTINGS', path, settings)
        return
    }

    const properties = settingsSchema.properties || {}

    Object.keys(settings).forEach(key => {
        const settingPath = `${path}.${key}`

        if (settingsSchema.additionalProperties === false && !hasOwn(properties, key)) {
            addError(errors, 'UNKNOWN_STEP_SETTING', settingPath, settings[key])
            return
        }

        validateValueAgainstSchema(settings[key], properties[key], settingPath, errors)
    })
}

const validateCondition = (id, path, conditionRegistry, errors) => {
    if (id === null) {
        return
    }

    if (typeof id !== 'string' || id.length === 0) {
        addError(errors, 'INVALID_CONDITION_ID', path, id)
        return
    }

    if (!conditionRegistry.has(id)) {
        addError(errors, 'UNKNOWN_CONDITION', path, id)
    }
}

const validateStep = ({
    step,
    index,
    stepRegistry,
    conditionRegistry,
    errors,
    typeCounts,
}) => {
    const path = `steps[${index}]`

    if (!isPlainObject(step)) {
        addError(errors, 'INVALID_STEP', path, step)
        return
    }

    if (typeof step.id !== 'string' || step.id.length === 0) {
        addError(errors, 'INVALID_STEP_ID', `${path}.id`, step.id)
    }

    if (typeof step.type !== 'string' || step.type.length === 0) {
        addError(errors, 'INVALID_STEP_TYPE', `${path}.type`, step.type)
    }

    if (!Number.isInteger(step.typeVersion) || step.typeVersion < 1) {
        addError(errors, 'INVALID_STEP_TYPE_VERSION', `${path}.typeVersion`, step.typeVersion)
    }

    if (!Number.isInteger(step.order)) {
        addError(errors, 'INVALID_STEP_ORDER', `${path}.order`, step.order)
    }

    if (typeof step.enabled !== 'boolean') {
        addError(errors, 'INVALID_STEP_ENABLED', `${path}.enabled`, step.enabled)
    }

    if (step.titleOverride !== null && typeof step.titleOverride !== 'string') {
        addError(errors, 'INVALID_STEP_TITLE_OVERRIDE', `${path}.titleOverride`, step.titleOverride)
    }

    if (
        typeof step.descriptionOverride !== 'undefined'
        && step.descriptionOverride !== null
        && typeof step.descriptionOverride !== 'string'
    ) {
        addError(errors, 'INVALID_STEP_DESCRIPTION_OVERRIDE', `${path}.descriptionOverride`, step.descriptionOverride)
    }

    if (
        typeof step.navigationTitleOverride !== 'undefined'
        && step.navigationTitleOverride !== null
        && typeof step.navigationTitleOverride !== 'string'
    ) {
        addError(
            errors,
            'INVALID_STEP_NAVIGATION_TITLE_OVERRIDE',
            `${path}.navigationTitleOverride`,
            step.navigationTitleOverride,
        )
    }

    if (typeof step.numbered !== 'boolean') {
        addError(errors, 'INVALID_STEP_NUMBERED', `${path}.numbered`, step.numbered)
    }

    if (typeof step.review !== 'undefined') {
        if (!isPlainObject(step.review)) {
            addError(errors, 'INVALID_STEP_REVIEW', `${path}.review`, step.review)
        } else {
            const allowedReviewKeys = ['enabled', 'initiallyExpanded', 'icon']

            Object.keys(step.review).forEach(key => {
                if (!allowedReviewKeys.includes(key)) {
                    addError(
                        errors,
                        'UNKNOWN_STEP_REVIEW_SETTING',
                        `${path}.review.${key}`,
                        step.review[key],
                    )
                }
            })

            if (typeof step.review.enabled !== 'boolean') {
                addError(
                    errors,
                    'INVALID_STEP_REVIEW_ENABLED',
                    `${path}.review.enabled`,
                    step.review.enabled,
                )
            }

            if (
                typeof step.review.initiallyExpanded !== 'undefined'
                && typeof step.review.initiallyExpanded !== 'boolean'
            ) {
                addError(
                    errors,
                    'INVALID_STEP_REVIEW_INITIAL_EXPANSION',
                    `${path}.review.initiallyExpanded`,
                    step.review.initiallyExpanded,
                )
            }

            if (
                typeof step.review.icon !== 'undefined'
                && (typeof step.review.icon !== 'string' || step.review.icon.length === 0)
            ) {
                addError(
                    errors,
                    'INVALID_STEP_REVIEW_ICON',
                    `${path}.review.icon`,
                    step.review.icon,
                )
            }
        }
    }

    const registration = stepRegistry.get(step.type, step.typeVersion)

    if (typeof step.type === 'string' && Number.isInteger(step.typeVersion)) {
        if (!stepRegistry.hasType(step.type)) {
            addError(errors, 'UNKNOWN_STEP_TYPE', `${path}.type`, step.type)
        } else if (!registration) {
            addError(errors, 'UNSUPPORTED_STEP_TYPE_VERSION', `${path}.typeVersion`, step.typeVersion)
        }
    }

    if (registration) {
        const typeKey = `${step.type}@${step.typeVersion}`
        const count = typeCounts.get(typeKey) || 0

        typeCounts.set(typeKey, count + 1)

        if (!registration.descriptor.capabilities.repeatable && count > 0) {
            addError(errors, 'NON_REPEATABLE_STEP_TYPE', `${path}.type`, step.type)
        }

        validateStepSettings(
            step.settings,
            registration.descriptor.settingsSchema,
            `${path}.settings`,
            errors,
        )

        if (
            step.review
            && step.review.enabled === true
            && typeof registration.createReviewContribution !== 'function'
        ) {
            addError(
                errors,
                'STEP_REVIEW_NOT_SUPPORTED',
                `${path}.review.enabled`,
                step.review.enabled,
            )
        }
    } else if (typeof step.settings !== 'undefined' && !isJsonValue(step.settings)) {
        addError(errors, 'INVALID_STEP_SETTINGS', `${path}.settings`, step.settings)
    }

    const { availability } = step

    if (!isPlainObject(availability)) {
        addError(errors, 'INVALID_STEP_AVAILABILITY', `${path}.availability`, availability)
    } else {
        validateCondition(
            availability.visibleWhen,
            `${path}.availability.visibleWhen`,
            conditionRegistry,
            errors,
        )
        validateCondition(
            availability.disabledWhen,
            `${path}.availability.disabledWhen`,
            conditionRegistry,
            errors,
        )
    }

    if (!isPlainObject(step.validation) || !hasEnumValue(STEP_VALIDATION_MODES, step.validation.mode)) {
        addError(errors, 'INVALID_STEP_VALIDATION_MODE', `${path}.validation.mode`, step.validation && step.validation.mode)
    }

    if (!isPlainObject(step.completion) || !hasEnumValue(STEP_COMPLETION_MODES, step.completion.mode)) {
        addError(errors, 'INVALID_STEP_COMPLETION_MODE', `${path}.completion.mode`, step.completion && step.completion.mode)
    }

    if (
        isPlainObject(step.completion)
        && typeof step.completion.persistProgress !== 'undefined'
        && typeof step.completion.persistProgress !== 'boolean'
    ) {
        addError(
            errors,
            'INVALID_STEP_PROGRESS_PERSISTENCE',
            `${path}.completion.persistProgress`,
            step.completion.persistProgress,
        )
    }

    if (
        isPlainObject(step.completion)
        && typeof step.completion.validationScope !== 'undefined'
        && !hasEnumValue(TERMINAL_VALIDATION_SCOPES, step.completion.validationScope)
    ) {
        addError(
            errors,
            'INVALID_TERMINAL_VALIDATION_SCOPE',
            `${path}.completion.validationScope`,
            step.completion.validationScope,
        )
    }

    if (
        isPlainObject(step.completion)
        && typeof step.completion.invalidBehavior !== 'undefined'
        && !hasEnumValue(TERMINAL_INVALID_BEHAVIORS, step.completion.invalidBehavior)
    ) {
        addError(
            errors,
            'INVALID_TERMINAL_INVALID_BEHAVIOR',
            `${path}.completion.invalidBehavior`,
            step.completion.invalidBehavior,
        )
    }

    if (
        isPlainObject(step.completion)
        && typeof step.completion.scrollAfterExecution !== 'undefined'
        && typeof step.completion.scrollAfterExecution !== 'boolean'
    ) {
        addError(
            errors,
            'INVALID_TERMINAL_SCROLL_AFTER_EXECUTION',
            `${path}.completion.scrollAfterExecution`,
            step.completion.scrollAfterExecution,
        )
    }

    if (
        isPlainObject(step.completion)
        && typeof step.completion.requiredForTerminal !== 'undefined'
        && typeof step.completion.requiredForTerminal !== 'boolean'
    ) {
        addError(
            errors,
            'INVALID_STEP_TERMINAL_REQUIREMENT',
            `${path}.completion.requiredForTerminal`,
            step.completion.requiredForTerminal,
        )
    }

    if (!isPlainObject(step.mount) || !hasEnumValue(STEP_MOUNT_MODES, step.mount.mode)) {
        addError(errors, 'INVALID_STEP_MOUNT_MODE', `${path}.mount.mode`, step.mount && step.mount.mode)
    }

    if (
        !isPlainObject(step.navigation)
        || !hasEnumValue(UNAVAILABLE_BEHAVIORS, step.navigation.unavailableBehavior)
    ) {
        addError(errors, 'INVALID_UNAVAILABLE_BEHAVIOR', `${path}.navigation.unavailableBehavior`, step.navigation && step.navigation.unavailableBehavior)
    }

    if (
        isPlainObject(step.navigation)
        && typeof step.navigation.scrollOnOpen !== 'undefined'
        && !hasEnumValue(NAVIGATION_SCROLL_POLICIES, step.navigation.scrollOnOpen)
    ) {
        addError(
            errors,
            'INVALID_STEP_NAVIGATION_SCROLL_POLICY',
            `${path}.navigation.scrollOnOpen`,
            step.navigation.scrollOnOpen,
        )
    }

    if (typeof step.legacy !== 'undefined') {
        if (!isPlainObject(step.legacy) || !Number.isInteger(step.legacy.persistedStep) || step.legacy.persistedStep < 1) {
            addError(errors, 'INVALID_LEGACY_PERSISTED_STEP', `${path}.legacy.persistedStep`, step.legacy && step.legacy.persistedStep)
        }
    }

    if (step.completion && step.validation) {
        const { mode: completionMode } = step.completion
        const { mode: validationMode } = step.validation

        if (completionMode === 'button' && validationMode === 'external') {
            addError(errors, 'INVALID_VALIDATION_COMPLETION_COMBINATION', path, {
                validationMode,
                completionMode,
            })
        }

        if (completionMode === 'external' && validationMode !== 'external') {
            addError(errors, 'INVALID_VALIDATION_COMPLETION_COMBINATION', path, {
                validationMode,
                completionMode,
            })
        }

        if (completionMode === 'terminal' && validationMode !== 'none') {
            addError(errors, 'INVALID_VALIDATION_COMPLETION_COMBINATION', path, {
                validationMode,
                completionMode,
            })
        }
    }
}

const validateFlowManifest = (manifest, { stepRegistry, conditionRegistry }) => {
    if (!stepRegistry || !conditionRegistry) {
        throw new Error('validateFlowManifest requires Step Registry and Condition Registry')
    }

    const errors = []

    if (!isPlainObject(manifest)) {
        addError(errors, 'INVALID_MANIFEST', '', manifest)
        return { isValid: false, errors }
    }

    if (!isJsonValue(manifest)) {
        addError(errors, 'NON_JSON_MANIFEST_VALUE', '', manifest)
    }

    if (manifest.schemaVersion !== FLOW_MANIFEST_SCHEMA_VERSION) {
        addError(errors, 'UNSUPPORTED_MANIFEST_SCHEMA_VERSION', 'schemaVersion', manifest.schemaVersion)
    }

    if (typeof manifest.id !== 'string' || manifest.id.length === 0) {
        addError(errors, 'INVALID_FLOW_ID', 'id', manifest.id)
    }

    if (!Number.isInteger(manifest.version) || manifest.version < 1) {
        addError(errors, 'INVALID_FLOW_VERSION', 'version', manifest.version)
    }

    if (typeof manifest.title !== 'undefined' && typeof manifest.title !== 'string') {
        addError(errors, 'INVALID_FLOW_TITLE', 'title', manifest.title)
    }

    if (!isPlainObject(manifest.settings)) {
        addError(errors, 'INVALID_FLOW_SETTINGS', 'settings', manifest.settings)
    } else {
        if (
            typeof manifest.settings.renderer !== 'undefined'
            && !hasEnumValue(FLOW_RENDERERS, manifest.settings.renderer)
        ) {
            addError(errors, 'INVALID_FLOW_RENDERER', 'settings.renderer', manifest.settings.renderer)
        }

        const { rendererSettings } = manifest.settings

        if (typeof rendererSettings !== 'undefined' && !isPlainObject(rendererSettings)) {
            addError(errors, 'INVALID_FLOW_RENDERER_SETTINGS', 'settings.rendererSettings', rendererSettings)
        } else if (isPlainObject(rendererSettings)) {
            Object.keys(rendererSettings).forEach(key => {
                if (!FLOW_RENDERER_SETTING_NAMES.includes(key)) {
                    addError(
                        errors,
                        'UNKNOWN_FLOW_RENDERER_SETTING',
                        `settings.rendererSettings.${key}`,
                        rendererSettings[key],
                    )
                }
            })

            if (
                typeof rendererSettings.contentContainer !== 'undefined'
                && !hasEnumValue(FLOW_CONTENT_CONTAINERS, rendererSettings.contentContainer)
            ) {
                addError(
                    errors,
                    'INVALID_FLOW_CONTENT_CONTAINER',
                    'settings.rendererSettings.contentContainer',
                    rendererSettings.contentContainer,
                )
            }

            if (
                typeof rendererSettings.stepperSize !== 'undefined'
                && !hasEnumValue(FLOW_STEPPER_SIZES, rendererSettings.stepperSize)
            ) {
                addError(
                    errors,
                    'INVALID_FLOW_STEPPER_SIZE',
                    'settings.rendererSettings.stepperSize',
                    rendererSettings.stepperSize,
                )
            }

            if (
                typeof rendererSettings.saveStatus !== 'undefined'
                && !hasEnumValue(FLOW_SAVE_STATUS_MODES, rendererSettings.saveStatus)
            ) {
                addError(
                    errors,
                    'INVALID_FLOW_SAVE_STATUS',
                    'settings.rendererSettings.saveStatus',
                    rendererSettings.saveStatus,
                )
            }

            FLOW_RENDERER_BOOLEAN_SETTINGS.forEach(key => {
                if (
                    typeof rendererSettings[key] !== 'undefined'
                    && typeof rendererSettings[key] !== 'boolean'
                ) {
                    addError(
                        errors,
                        'INVALID_FLOW_RENDERER_SETTING',
                        `settings.rendererSettings.${key}`,
                        rendererSettings[key],
                    )
                }
            })
        }

        if (typeof manifest.settings.accordion !== 'boolean') {
            addError(errors, 'INVALID_FLOW_ACCORDION_SETTING', 'settings.accordion', manifest.settings.accordion)
        }

        if (typeof manifest.settings.showStepNumbers !== 'boolean') {
            addError(errors, 'INVALID_FLOW_SHOW_STEP_NUMBERS_SETTING', 'settings.showStepNumbers', manifest.settings.showStepNumbers)
        }

        if (!hasEnumValue(STEP_REVEAL_MODES, manifest.settings.revealMode)) {
            addError(errors, 'INVALID_FLOW_REVEAL_MODE', 'settings.revealMode', manifest.settings.revealMode)
        }

        if (
            typeof manifest.settings.nextStepOpenDelayMs !== 'undefined'
            && (!Number.isInteger(manifest.settings.nextStepOpenDelayMs) || manifest.settings.nextStepOpenDelayMs < 0)
        ) {
            addError(errors, 'INVALID_FLOW_NEXT_STEP_OPEN_DELAY', 'settings.nextStepOpenDelayMs', manifest.settings.nextStepOpenDelayMs)
        }

        if (
            typeof manifest.settings.scrollToNextStep !== 'undefined'
            && typeof manifest.settings.scrollToNextStep !== 'boolean'
        ) {
            addError(errors, 'INVALID_FLOW_SCROLL_TO_NEXT_STEP', 'settings.scrollToNextStep', manifest.settings.scrollToNextStep)
        }

        if (
            typeof manifest.settings.nextStepScrollDelayMs !== 'undefined'
            && (!Number.isInteger(manifest.settings.nextStepScrollDelayMs) || manifest.settings.nextStepScrollDelayMs < 0)
        ) {
            addError(errors, 'INVALID_FLOW_NEXT_STEP_SCROLL_DELAY', 'settings.nextStepScrollDelayMs', manifest.settings.nextStepScrollDelayMs)
        }

        const navigationSettings = manifest.settings.navigation

        if (typeof navigationSettings !== 'undefined' && !isPlainObject(navigationSettings)) {
            addError(
                errors,
                'INVALID_FLOW_NAVIGATION_SETTINGS',
                'settings.navigation',
                navigationSettings,
            )
        } else if (isPlainObject(navigationSettings)) {
            Object.keys(navigationSettings).forEach(key => {
                if (!['scrollOnOpen', 'blockForwardOnInvalidSteps'].includes(key)) {
                    addError(
                        errors,
                        'UNKNOWN_FLOW_NAVIGATION_SETTING',
                        `settings.navigation.${key}`,
                        navigationSettings[key],
                    )
                }
            })

            if (
                typeof navigationSettings.scrollOnOpen !== 'undefined'
                && !hasEnumValue(NAVIGATION_SCROLL_POLICIES, navigationSettings.scrollOnOpen)
            ) {
                addError(
                    errors,
                    'INVALID_FLOW_NAVIGATION_SCROLL_POLICY',
                    'settings.navigation.scrollOnOpen',
                    navigationSettings.scrollOnOpen,
                )
            }

            if (
                typeof navigationSettings.blockForwardOnInvalidSteps !== 'undefined'
                && typeof navigationSettings.blockForwardOnInvalidSteps !== 'boolean'
            ) {
                addError(
                    errors,
                    'INVALID_FLOW_NAVIGATION_VALIDATION_BARRIER',
                    'settings.navigation.blockForwardOnInvalidSteps',
                    navigationSettings.blockForwardOnInvalidSteps,
                )
            }
        }

        if (
            typeof manifest.settings.progressPersistenceMode !== 'undefined'
            && !hasEnumValue(FLOW_PROGRESS_PERSISTENCE_MODES, manifest.settings.progressPersistenceMode)
        ) {
            addError(errors, 'INVALID_FLOW_PROGRESS_PERSISTENCE_MODE', 'settings.progressPersistenceMode', manifest.settings.progressPersistenceMode)
        }

        if (
            typeof manifest.settings.initialStepMode !== 'undefined'
            && !hasEnumValue(FLOW_INITIAL_STEP_MODES, manifest.settings.initialStepMode)
        ) {
            addError(errors, 'INVALID_FLOW_INITIAL_STEP_MODE', 'settings.initialStepMode', manifest.settings.initialStepMode)
        }

        if (
            manifest.settings.renderer === 'horizontal-stepper'
            && manifest.settings.accordion !== true
        ) {
            addError(errors, 'HORIZONTAL_STEPPER_REQUIRES_ACCORDION', 'settings.accordion', manifest.settings.accordion)
        }
    }

    if (!Array.isArray(manifest.steps) || manifest.steps.length === 0) {
        addError(errors, 'INVALID_FLOW_STEPS', 'steps', manifest.steps)
        return { isValid: false, errors }
    }

    const stepIds = new Set()
    const orders = new Set()
    const persistedSteps = new Set()
    const typeCounts = new Map()
    const terminalIndexes = []

    manifest.steps.forEach((step, index) => {
        validateStep({
            step,
            index,
            stepRegistry,
            conditionRegistry,
            errors,
            typeCounts,
        })

        if (!isPlainObject(step)) {
            return
        }

        if (stepIds.has(step.id)) {
            addError(errors, 'DUPLICATE_STEP_ID', `steps[${index}].id`, step.id)
        }
        stepIds.add(step.id)

        if (orders.has(step.order)) {
            addError(errors, 'DUPLICATE_STEP_ORDER', `steps[${index}].order`, step.order)
        }
        orders.add(step.order)

        if (step.legacy && persistedSteps.has(step.legacy.persistedStep)) {
            addError(errors, 'DUPLICATE_LEGACY_PERSISTED_STEP', `steps[${index}].legacy.persistedStep`, step.legacy.persistedStep)
        }
        if (step.legacy) {
            persistedSteps.add(step.legacy.persistedStep)
        }

        if (step.completion && step.completion.mode === 'terminal') {
            terminalIndexes.push(index)

            if (step.numbered !== false) {
                addError(errors, 'TERMINAL_STEP_MUST_BE_UNNUMBERED', `steps[${index}].numbered`, step.numbered)
            }
        }
    })

    if (terminalIndexes.length > 1) {
        terminalIndexes.slice(1).forEach(index => {
            addError(errors, 'MULTIPLE_TERMINAL_STEPS', `steps[${index}].completion.mode`, 'terminal')
        })
    }

    if (terminalIndexes.length === 1) {
        const terminalIndex = terminalIndexes[0]
        const terminalStep = manifest.steps[terminalIndex]
        const maxOrder = Math.max(...manifest.steps
            .filter(isPlainObject)
            .map(step => step.order)
            .filter(Number.isInteger))

        if (terminalStep.order !== maxOrder) {
            addError(errors, 'TERMINAL_STEP_MUST_BE_LAST', `steps[${terminalIndex}].completion.mode`, 'terminal')
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
    }
}

export { validateFlowManifest }
export default validateFlowManifest
